import React from "react";
import { MotiView, AnimatePresence } from "moti";
import { StyleSheet } from "react-native";

import AssignmentPanel from "./AssignmentPanel";
import ExternalLinkPanel from "./ExternalLinkPanel";
import ModuleDescriptionPanel from "./ModuleDescriptionPanel";
import RecommendationPanel from "./RecommendationPanel";
import SuggestedContentPanel from "./SuggestedContentPanel";
import OverviewPanel from "./OverviewPanel";
import type { CourseEvaluatorReviewData, CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";
import type { ActivityDetail, StructuredFields } from "../types/reviewTypes";

interface ReviewContentPanelProps {
  review: CourseEvaluatorReviewData;
  selectedReviewItem: CourseEvaluatorReviewSidebarItem | null;
  activityDetails: Record<string, ActivityDetail>;
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onStartReviewing: () => void;
  onOverlayVisibilityChange?: (isVisible: boolean) => void;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onAcceptWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onRejectWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
}

function ReviewContentPanel({
  review,
  selectedReviewItem,
  activityDetails,
  isExpanded,
  isEditDisabled,
  onStartReviewing,
  onOverlayVisibilityChange,
  onSaveEdit,
  onCreateIle,
  onAccept,
  onReject,
  onAcceptWithoutNav,
  onRejectWithoutNav,
  onJumpToEntity,
}: ReviewContentPanelProps) {
  const itemKey = selectedReviewItem?.id ?? "overview";

  return (
    <AnimatePresence exitBeforeEnter>
      <MotiView
        key={itemKey}
        style={isExpanded && selectedReviewItem ? styles.expandedRoot : undefined}
        from={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ type: "timing", duration: 150 }}>
        {selectedReviewItem === null ? (
          <OverviewPanel
            review={review}
            onStartReviewing={onStartReviewing}
            onOverlayVisibilityChange={onOverlayVisibilityChange}
            onAccept={onAccept}
            onReject={onReject}
            onAcceptWithoutNav={onAcceptWithoutNav}
            onRejectWithoutNav={onRejectWithoutNav}
            onJumpToEntity={onJumpToEntity}
          />
        ) : (
          <PanelContent
            selectedReviewItem={selectedReviewItem}
            activityDetails={activityDetails}
            isExpanded={isExpanded}
            isEditDisabled={isEditDisabled}
            onSaveEdit={onSaveEdit}
            onCreateIle={onCreateIle}
            sourceLabel={review.sourceLabel}
          />
        )}
      </MotiView>
    </AnimatePresence>
  );
}

/** Inner component to keep the switch logic clean */
function PanelContent({
  selectedReviewItem,
  activityDetails,
  isExpanded,
  isEditDisabled,
  onSaveEdit,
  onCreateIle,
  sourceLabel,
}: {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetails: Record<string, ActivityDetail>;
  isExpanded?: boolean;
  isEditDisabled?: boolean;
  onSaveEdit?: (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => void;
  onCreateIle?: (item: CourseEvaluatorReviewSidebarItem) => void;
  sourceLabel: string;
}) {
  const activityDetail = activityDetails[selectedReviewItem.id] ?? null;
  const handleSaveEdit = onSaveEdit ? (fields: StructuredFields) => onSaveEdit(selectedReviewItem, fields) : undefined;
  const handleCreateIle = onCreateIle ? () => onCreateIle(selectedReviewItem) : undefined;

  switch (selectedReviewItem.kind) {
    case "moduleDescription":
      return (
        <ModuleDescriptionPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
          sourceLabel={sourceLabel}
        />
      );
    case "recommendation":
      return (
        <RecommendationPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
        />
      );
    case "assignment": {
      const itemTypeMeta = selectedReviewItem.meta?.find((m) => m.kind === "itemType");
      const itemType = itemTypeMeta?.kind === "itemType" ? itemTypeMeta.itemType : undefined;
      const isExternalLink =
        itemType === "externalLink" ||
        itemType === "link" ||
        activityDetail?.activityType === "externalLink" ||
        activityDetail?.activityType === "link";
      if (isExternalLink) {
        return (
          <ExternalLinkPanel
            selectedReviewItem={selectedReviewItem}
            activityDetail={activityDetail}
            isExpanded={isExpanded}
            isEditDisabled={isEditDisabled}
            onSaveEdit={handleSaveEdit}
          />
        );
      }
      return (
        <AssignmentPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
        />
      );
    }
    case "suggestedContent":
      return (
        <SuggestedContentPanel
          selectedReviewItem={selectedReviewItem}
          activityDetail={activityDetail}
          isExpanded={isExpanded}
          isEditDisabled={isEditDisabled}
          onSaveEdit={handleSaveEdit}
          onCreateIle={handleCreateIle}
        />
      );
    default:
      return null;
  }
}

const styles = StyleSheet.create({
  expandedRoot: {
    flex: 1,
    minHeight: 0,
  },
});

export default ReviewContentPanel;
